import { Box, IconButton, Tooltip as MuiTooltip, Typography, useTheme } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { RechartsDevtools } from "@recharts/devtools";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Cell, Label, Legend, Pie, PieChart as RePieChart, ResponsiveContainer, Tooltip } from "recharts";
import DownloadComponentButton from "../button/DownloadComponentButton";
import { formatTo2Decimals } from "../grid/columnDefinitions/columnDefinitions";
import { useGetColor } from "./Colors";
import { GRAPH_PALETTE, getGraphColorByStage } from "../../theme/style/graphChartPalette";
import { getOrgTokens } from "../../theme/style/organizationPalette";

export default function PieChart({ title, data, hollow = false, InnerTextComponent = null, onClick = () => { }, outerRadius = "80%", SideContent = null, collapsibleSideContent = false, sideContentDefaultOpen = true }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tok = getOrgTokens(isDark);
  const [visibility, setVisibility] = useState(false);
  const [hoveringDataKey, setHoveringDataKey] = useState(null);
  const dataValueSum = useMemo(() => data.reduce((sum, d) => sum + d.value, 0), [data]);
  const ref = useRef(null);
  const getColor = useGetColor();
  const legendOnMouseEnter = (payload) => {
    setHoveringDataKey(payload.value);
  };
  const pieOnMouseEnter = (payload) => {
    setHoveringDataKey(payload.label);
  };
  const onMouseLeave = () => {
    setHoveringDataKey(null);
  };
  const innerRadius = useMemo(() => (hollow ? "58%" : 0), [hollow]);
  const [isSideContentOpen, setIsSideContentOpen] = useState(collapsibleSideContent ? sideContentDefaultOpen : true);
  const visibleSideContent = SideContent && isSideContentOpen ? SideContent : null;
  
  return (
    <Box
      sx={{ position: "relative", height: "100%", width: "100%" }}
      onMouseEnter={() => setVisibility(true)}
      onMouseLeave={() => setVisibility(false)}
    >
      <Box style={{ position: "absolute", top: "5px", right: "5px", zIndex: 10 }}>
        <DownloadComponentButton ref={ref} filename={(title || "chart") + ".png"} visibility={visibility} />
      </Box>
      <Box 
        ref={ref} 
        sx={{ 
          height: "100%", 
          width: "100%",
          display: "flex", 
          flexDirection: "column", 
          backgroundColor: tok.bg.card 
        }}
      >
        {title && (
          <Box sx={{ 
            display: "flex", 
            alignItems: "center", 
            px: 3, 
            py: 1.5, 
            borderBottom: 1, 
            borderColor: tok.border.divider,
            flexShrink: 0,
            zIndex: 1,
          }}>
            <Typography variant="Chart Title" sx={{ color: tok.text.primary, fontWeight: 600, textAlign: "left" }}>
              {t(title)}
            </Typography>
            {collapsibleSideContent && SideContent && (
              <MuiTooltip title={isSideContentOpen ? t("Hide description") : t("Show description")}>
                <IconButton
                  size="small"
                  onClick={() => setIsSideContentOpen((prev) => !prev)}
                  aria-pressed={isSideContentOpen}
                  sx={{ ml: 1, color: isSideContentOpen ? tok.bg.tabActive : tok.text.secondary }}
                >
                  {isSideContentOpen ? <VisibilityOffOutlinedIcon fontSize="small" /> : <VisibilityOutlinedIcon fontSize="small" />}
                </IconButton>
              </MuiTooltip>
            )}
          </Box>
        )}
        <Box sx={{ 
          flexGrow: 1, 
          display: "flex", 
          flexDirection: visibleSideContent ? { xs: "column", md: "row" } : "column", 
          alignItems: "stretch", 
          px: 2, 
          pb: 2, 
          pt: 1,
          minHeight: 0,
          height: "100%",
          gap: 2,
        }}>
          <Box sx={{ 
            flex: visibleSideContent ? "1 1 50%" : "1 1 100%", 
            minWidth: 0,
            height: "100%", 
            minHeight: 280, 
            position: "relative" 
          }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={280}>
              <RePieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="label"
                  cx="50%"
                  cy="50%"
                  outerRadius={outerRadius}
                  fill="#82ca9d"
                  label={false}
                  isAnimationActive={false}
                  onMouseEnter={pieOnMouseEnter}
                  onMouseLeave={onMouseLeave}
                  innerRadius={innerRadius}
                  onClick={onClick}
                >
                  {data.map((d, index) => {
                    const stageColor = getGraphColorByStage(d.label);
                    const finalColor = stageColor !== GRAPH_PALETTE.lcaStages.Fallback ? stageColor : getColor(index, data.length);
                    return (
                      <Cell key={`cell-${index}`} fill={finalColor} opacity={d.label === hoveringDataKey ? 1 : 0.7} />
                    );
                  })}
                </Pie>
                <Tooltip
                  contentStyle={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: GRAPH_PALETTE.font.sizes.standard, borderRadius: "8px" }}
                  formatter={(value, name) => {
                    const percent = dataValueSum ? (value / dataValueSum) * 100 : 0;
                    return [`${percent.toFixed(2)}% (Score: ${formatTo2Decimals(value)})`, t(name)];
                  }}
                />
                <Legend
                  formatter={(value) => t(value)}
                  wrapperStyle={{ fontFamily: GRAPH_PALETTE.font.family, fontSize: 14 }}
                  onMouseEnter={legendOnMouseEnter}
                  onMouseLeave={onMouseLeave}
                />
                <Label
                  position="center"
                  content={(props) => {
                    const { x, y, width, height } = props.viewBox;
                    const cx = x + width / 2;
                    const cy = y + height / 2;
                    const outerR = Math.min(width, height) / 2;
                    const innerR = outerR * (hollow ? 0.58 : 0.5);
                    const w = innerR * 1.8;
                    const h = innerR * 1.8;
                    return (
                      <foreignObject x={cx - w / 2} y={cy - h / 2} width={w} height={h} style={{ overflow: "visible" }}>
                        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {InnerTextComponent}
                        </div>
                      </foreignObject>
                    );
                  }}
                />
                <RechartsDevtools />
              </RePieChart>
            </ResponsiveContainer>
          </Box>

          {visibleSideContent && (
            <Box sx={{ 
              flex: "1 1 50%", 
              minWidth: 0,
              display: "flex", 
              alignItems: "center", 
              justifyContent: "flex-start", 
              px: { xs: 1, md: 2 }, 
              py: 1, 
              textAlign: "left",
              overflowY: "auto",
              maxHeight: "100%",
            }}>
              {visibleSideContent}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
